/**
 * Vizualni elementi nadzorne ploce modula 72 (KYC / AML).
 *
 * Namjerno bez vanjske chart biblioteke: BIS nema recharts/chart.js u
 * package.json, a jedina postojeca "biblioteka" (modules/sharedCharts.tsx) je
 * rucno pisani SVG. Sve ovdje je isti pristup - SVG za kruzne i linijske
 * prikaze, flexbox za stupce - pa nema nove ovisnosti ni novog build tereta.
 *
 * Komponente su ciste (bez API poziva i bez stanja): boje i podatke dobivaju
 * kroz props, da bi paleta ostala u kycModule.tsx i da se prikaz ne razilazi
 * s ostatkom modula.
 */
import React from 'react';

export type Slice = { key: string; label: string; value: number; color: string };
export type Column = { label: string; value: number; accent?: number; title?: string };

const nf = new Intl.NumberFormat('hr-HR');
export const fmtInt = (n: number): string => nf.format(Math.round(n || 0));

/** Postotak kao cijeli broj, bez dijeljenja s nulom. */
export const pct = (part: number, total: number): number => (total > 0 ? Math.round((part / total) * 100) : 0);

// ── IKONE ──────────────────────────────────────────────────────────────────────
// Minimalan skup linijskih ikona (stroke, currentColor). Bez emojija i bez
// dodatnog fonta; svaka je 24x24 grid da se poklapaju u nizu.
export type IconName = 'shield' | 'alert' | 'flag' | 'cash' | 'clock' | 'folder' | 'check' | 'trend' | 'scale' | 'users' | 'bolt' | 'lock';

const ICON_PATHS: Record<IconName, React.ReactNode> = {
    shield: <path d="M12 3l7 3v5.2c0 4.3-2.9 7.9-7 9.3-4.1-1.4-7-5-7-9.3V6l7-3z" />,
    alert: <><path d="M12 4.5L21 20H3L12 4.5z" /><path d="M12 10v4.2" /><path d="M12 17.1v.1" /></>,
    flag: <><path d="M5 21V4" /><path d="M5 5h11l-1.8 3.4L16 12H5" /></>,
    cash: <><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="12" cy="12" r="2.6" /><path d="M6.5 12h.1M17.4 12h.1" /></>,
    clock: <><circle cx="12" cy="12" r="8.4" /><path d="M12 7.4V12l3 1.9" /></>,
    folder: <path d="M3 7.5a1.5 1.5 0 011.5-1.5h4l2 2.2h8A1.5 1.5 0 0120 9.7v7.8a1.5 1.5 0 01-1.5 1.5h-14A1.5 1.5 0 013 17.5z" />,
    check: <><circle cx="12" cy="12" r="8.4" /><path d="M8.3 12.3l2.5 2.5 4.9-5.2" /></>,
    trend: <><path d="M3.5 16.5l5.2-5.2 3.4 3.4L20.5 7" /><path d="M15.6 7h4.9v4.9" /></>,
    scale: <><path d="M12 4.5v15" /><path d="M6 8h12" /><path d="M6 8l-2.6 5.4h5.2zM18 8l-2.6 5.4h5.2z" /><path d="M8.4 19.5h7.2" /></>,
    users: <><circle cx="9.4" cy="8.6" r="3.2" /><path d="M3.6 19.4c0-3.2 2.6-5.4 5.8-5.4s5.8 2.2 5.8 5.4" /><path d="M16.2 6.1a3.1 3.1 0 010 5.9" /><path d="M17.4 14.3c2 .6 3.2 2.4 3.2 5.1" /></>,
    bolt: <path d="M13.2 3L5.6 13.4h5.1L10.2 21l7.9-10.6h-5.3z" />,
    lock: <><rect x="4.6" y="10.4" width="14.8" height="9.4" rx="2" /><path d="M8.3 10.4V7.9a3.7 3.7 0 017.4 0v2.5" /></>,
};

export function Icon({ name, size = 20, strokeWidth = 1.7, style }: { name: IconName; size?: number; strokeWidth?: number; style?: React.CSSProperties }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ display: 'block', flex: 'none', ...style }}>
            {ICON_PATHS[name]}
        </svg>
    );
}

// ── PRSTEN (donut) ─────────────────────────────────────────────────────────────
/**
 * Raspodjela u prstenu s brojkom u sredini. Kad je zbroj nula crta se samo
 * sivi obrub, da ploca ne pokazuje prazan krug bez objasnjenja.
 */
export function DonutChart({ slices, size = 168, thickness = 20, centerValue, centerLabel, emptyLabel = 'Nema podataka' }: {
    slices: Slice[]; size?: number; thickness?: number; centerValue?: string | number; centerLabel?: string; emptyLabel?: string;
}) {
    const total = slices.reduce((s, x) => s + (x.value || 0), 0);
    const r = 50 - thickness / 2;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    return (
        <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
            <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={emptyLabel}>
                <circle cx="50" cy="50" r={r} fill="none" stroke="#e8edf3" strokeWidth={thickness} />
                {total > 0 && slices.filter((s) => s.value > 0).map((s) => {
                    const len = (s.value / total) * circ;
                    const dash = `${Math.max(len - 1.1, 0.6)} ${circ - Math.max(len - 1.1, 0.6)}`;
                    const el = (
                        <circle key={s.key} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth={thickness}
                            strokeDasharray={dash} strokeDashoffset={-offset} strokeLinecap="butt"
                            transform="rotate(-90 50 50)"><title>{`${s.label}: ${fmtInt(s.value)} (${pct(s.value, total)}%)`}</title></circle>
                    );
                    offset += len;
                    return el;
                })}
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ fontSize: size * 0.21, fontWeight: 800, lineHeight: 1, letterSpacing: '-.03em', fontVariantNumeric: 'tabular-nums' }}>{centerValue ?? fmtInt(total)}</div>
                {centerLabel && <div style={{ fontSize: 10.5, color: '#7b8899', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.09em', fontWeight: 700 }}>{centerLabel}</div>}
            </div>
        </div>
    );
}

/** Legenda uz prsten: kvadratic boje, naziv, brojka i postotak. */
export function Legend({ slices, onPick }: { slices: Slice[]; onPick?: (key: string) => void }) {
    const total = slices.reduce((s, x) => s + (x.value || 0), 0);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 150, flex: '1 1 150px' }}>
            {slices.map((s) => (
                <div key={s.key} onClick={onPick ? () => onPick(s.key) : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: onPick ? 'pointer' : 'default', padding: '3px 5px', borderRadius: 7 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color, flex: 'none', boxShadow: `0 0 0 3px ${s.color}1f` }} />
                    {/* Naziv se prelama umjesto da se krati: na uskom zaslonu je
                        "Neprocijen..." beskorisno, a jedan red vise nije problem.
                        overflowWrap je nuzan jer se jedna duga rijec bez njega ne
                        lomi nego prelije preko brojke desno od sebe. */}
                    <span style={{ fontSize: 12.5, color: '#455266', fontWeight: 600, flex: 1, minWidth: 0, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{s.label}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: '#1d2b3d', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(s.value)}</span>
                    <span style={{ fontSize: 11, color: '#8b98a9', width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pct(s.value, total)}%</span>
                </div>
            ))}
        </div>
    );
}

// ── STUPCI (vertikalni) ────────────────────────────────────────────────────────
/**
 * Stupci u flexboxu, ne SVG: tekst ostaje ostar na svakoj sirini, a stupci se
 * sami rastegnu. `accent` je dio stupca koji se boji naglasnom bojom (npr.
 * koliko je od ukupnih predmeta u mjesecu bilo visokog rizika).
 */
export function ColumnChart({ data, color, accentColor, height = 150, accentLabel, onPick }: {
    data: Column[]; color: string; accentColor?: string; height?: number; accentLabel?: string; onPick?: (index: number) => void;
}) {
    const max = Math.max(1, ...data.map((d) => d.value));
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height, marginTop: 8 }}>
                {data.map((d, i) => {
                    const h = Math.max((d.value / max) * 100, d.value > 0 ? 3 : 0.9);
                    const accentH = d.accent && d.value > 0 ? Math.min((d.accent / d.value) * 100, 100) : 0;
                    return (
                        <div key={`${d.label}-${i}`} title={d.title ?? `${d.label}: ${fmtInt(d.value)}`} onClick={onPick ? () => onPick(i) : undefined}
                            style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', cursor: onPick ? 'pointer' : 'default', minWidth: 0 }}>
                            <div style={{ fontSize: 10.5, fontWeight: 800, color: d.value > 0 ? '#3b4a5f' : '#b7c1cd', textAlign: 'center', marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>{d.value > 0 ? fmtInt(d.value) : ''}</div>
                            <div className="kyc-col" style={{ height: `${h}%`, background: d.value > 0 ? color : '#e6ebf1', borderRadius: '5px 5px 2px 2px', position: 'relative', overflow: 'hidden', minHeight: 3 }}>
                                {accentH > 0 && accentColor && <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${accentH}%`, background: accentColor }} />}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: 5, marginTop: 7 }}>
                {data.map((d, i) => <div key={`l-${d.label}-${i}`} style={{ flex: '1 1 0', fontSize: 9.5, color: '#8b98a9', textAlign: 'center', fontWeight: 650, whiteSpace: 'nowrap', overflow: 'hidden' }}>{d.label}</div>)}
            </div>
            {accentColor && accentLabel && (
                <div style={{ display: 'flex', gap: 14, marginTop: 9, fontSize: 11, color: '#7b8899', fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />{'ukupno'}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: accentColor }} />{accentLabel}</span>
                </div>
            )}
        </div>
    );
}

// ── VODORAVNE TRAKE ────────────────────────────────────────────────────────────
/** Rangirana lista s trakom - citljivija od tablice kad se usporeduju velicine. */
export function BarList({ items, emptyLabel, onPick }: {
    items: Array<{ key: string; label: string; value: number; color: string; sub?: string; badge?: string; accent?: number; accentColor?: string }>;
    emptyLabel?: string; onPick?: (key: string) => void;
}) {
    const max = Math.max(1, ...items.map((i) => i.value));
    if (items.length === 0) return <div style={{ color: '#8b98a9', fontSize: 12.5, padding: '14px 0' }}>{emptyLabel ?? 'Nema podataka.'}</div>;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
            {items.map((i) => (
                <div key={i.key} onClick={onPick ? () => onPick(i.key) : undefined} style={{ cursor: onPick ? 'pointer' : 'default' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 650, color: '#3b4a5f', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.label}</span>
                        {i.badge && <span style={{ fontSize: 10, fontWeight: 800, color: i.color, background: `${i.color}18`, borderRadius: 6, padding: '1px 6px' }}>{i.badge}</span>}
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#1d2b3d', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(i.value)}</span>
                    </div>
                    <div style={{ height: 9, background: '#eef2f6', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max((i.value / max) * 100, i.value > 0 ? 2 : 0)}%`, height: '100%', background: i.color, borderRadius: 5, transition: 'width .4s ease', display: 'flex', justifyContent: 'flex-end' }}>
                            {/* Naglasni dio (npr. visokorizicni predmeti) sjedi u traci, */}
                            {/* pa se udio vidi bez druge trake i bez druge legende.      */}
                            {i.accent && i.accent > 0 && i.accentColor
                                ? <div title={`${i.accent}`} style={{ width: `${Math.min((i.accent / Math.max(i.value, 1)) * 100, 100)}%`, height: '100%', background: i.accentColor, borderRadius: 5 }} />
                                : null}
                        </div>
                    </div>
                    {i.sub && <div style={{ fontSize: 10.5, color: '#8b98a9', marginTop: 3 }}>{i.sub}</div>}
                </div>
            ))}
        </div>
    );
}

/** Jedna traka podijeljena na udjele - za SLA presjek i tok statusa. */
export function StackedBar({ slices, height = 16, showLabels = true }: { slices: Slice[]; height?: number; showLabels?: boolean }) {
    const total = slices.reduce((s, x) => s + (x.value || 0), 0);
    return (
        <div>
            <div style={{ display: 'flex', height, borderRadius: height / 2, overflow: 'hidden', background: '#eef2f6' }}>
                {total > 0 && slices.filter((s) => s.value > 0).map((s) => (
                    <div key={s.key} title={`${s.label}: ${fmtInt(s.value)} (${pct(s.value, total)}%)`}
                        style={{ width: `${(s.value / total) * 100}%`, background: s.color, transition: 'width .4s ease' }} />
                ))}
            </div>
            {showLabels && (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 9 }}>
                    {slices.map((s) => (
                        <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#5c6b7f', fontWeight: 600 }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color }} />
                            {s.label}
                            <b style={{ color: '#1d2b3d', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(s.value)}</b>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── SPARKLINE ──────────────────────────────────────────────────────────────────
/** Mikro-linija u KPI kartici. Ista tehnika kao sharedCharts.tsx (non-scaling-stroke). */
export function Sparkline({ values, color, height = 26 }: { values: number[]; color: string; height?: number }) {
    if (values.length < 2) return <div style={{ height }} />;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const pts = values.map((v, i) => `${(i / (values.length - 1)) * 100},${28 - ((v - min) / range) * 24}`).join(' ');
    return (
        <svg viewBox="0 0 100 32" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block', overflow: 'visible' }} role="img" aria-hidden="true">
            <polygon points={`0,32 ${pts} 100,32`} fill={color} opacity={0.13} />
            <polyline points={pts} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

// ── MJERAC (gauge) ─────────────────────────────────────────────────────────────
/** Polukruzni mjerac za jedan postotak - koristi se za udio dosjea u roku. */
export function Gauge({ value, color, label, size = 132 }: { value: number; color: string; label?: string; size?: number }) {
    const v = Math.max(0, Math.min(100, value));
    const r = 42;
    const half = Math.PI * r;
    return (
        <div style={{ width: size, flex: 'none', textAlign: 'center' }}>
            <svg viewBox="0 0 100 56" width={size} height={size * 0.56} role="img" aria-label={label}>
                <path d={`M 8 50 A ${r} ${r} 0 0 1 92 50`} fill="none" stroke="#e8edf3" strokeWidth={11} strokeLinecap="round" />
                <path d={`M 8 50 A ${r} ${r} 0 0 1 92 50`} fill="none" stroke={color} strokeWidth={11} strokeLinecap="round"
                    strokeDasharray={`${(v / 100) * half} ${half}`} />
            </svg>
            <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: -8, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums' }}>{v}%</div>
            {label && <div style={{ fontSize: 10.5, color: '#7b8899', marginTop: 2, fontWeight: 650 }}>{label}</div>}
        </div>
    );
}
